/**
 * @file schemas/risk-annual.ts
 * @description 年度客戶風險評估（ZHQP27 附表4「危害鑑別與風險評估表」）共享契約。
 *
 * 業務背景（客戶 PPT S25/S26）：
 * - 每客戶每年度一筆評估，8 個評估項各 0-3 分，另有「嚴重度」權重（1/3/5/10）。
 * - 小計 = Σ 各項分數；合計 = 小計 × 嚴重度。
 * - 風險等級：低（<20）／中（20-40）／高（>40）。低風險以定期追蹤查驗方式持續監控。
 * - A 區（8 項評分 + 評分說明）由 AI 依客戶歷史數據自動判定，可人工修正；
 *   B 區（管制措施及因應對策／控管情形）由管理組填寫。
 * - 「逐年遞減」：今年風險分數高，建立次年度評估時各項分數預填遞減（3→2→1→0）。
 *
 * 本檔只放 zod schema、字典常量與純函數，供 API 與 Web 共用。
 */
import { z } from 'zod';
// ─── 8 個評估項字典 ───────────────────────────────────────────────────────────
/**
 * 評估項定義（key 唯一、順序即附表4 欄位順序①~⑧）。
 * `guide` 為評分說明檔位全文（0~3 分語義），供前端 tooltip / Drawer 展示，
 * 也是 AI 評分 scoreNote 的措辭基準。
 */
export const RISK_ANNUAL_ITEM_DEFS = [
    {
        key: 'prevNc',
        no: '①',
        label: '前次查驗NC結果',
        guide: '無（0分）；低：NC 1～2 件（1分）；中：NC 3～5 件（2分）；高：NC 5 件以上（3分）',
    },
    {
        key: 'prohibitedSubstance',
        no: '②',
        label: '使用禁用物質或被禁用物質污染',
        guide: '從來沒有（0分）；可能性低（1分）；可能性中（2分）；可能性高（3分）',
    },
    {
        key: 'geography',
        no: '③',
        label: '區域地理',
        guide: '無（0分）；緩衝區、面積大小、鄰田污染：低（1分）／中（2分）／高（3分）',
    },
    {
        key: 'product',
        no: '④',
        label: '產品',
        guide: '無（0分）；產出比、平行生產、產量、屠宰能力等：低（1分）／中（2分）／高（3分）',
    },
    {
        key: 'labeling',
        no: '⑤',
        label: '標章標示',
        guide: '標章使用管理、標示不清混雜風險、標示容易變換、套印標章使用不當：低（1分）～高（3分）；尚無標章使用（0分）',
    },
    {
        key: 'complexity',
        no: '⑥',
        label: '複雜度',
        guide: '國內外人員複雜、人員異動、供應鏈複雜、新申請客戶等（0～3分）',
    },
    {
        key: 'marketViolation',
        no: '⑦',
        label: '市售違規',
        guide: '無（0分）；曾經違規（1分）；矯正不徹底（3分）',
    },
    {
        key: 'overallPerformance',
        no: '⑧',
        label: '整體表現',
        guide: '稽核配合度、被投訴狀況、標章標識、內部管理不完善（0～3分）',
    },
];
/** 評估項 key 清單（zod enum / 迭代用） */
export const RISK_ANNUAL_ITEM_KEYS = [
    'prevNc',
    'prohibitedSubstance',
    'geography',
    'product',
    'labeling',
    'complexity',
    'marketViolation',
    'overallPerformance',
];
/** 評估項 key zod schema */
export const riskAnnualItemKeySchema = z.enum(RISK_ANNUAL_ITEM_KEYS);
/** 嚴重度權重可選檔位（附表4 固定 1/3/5/10） */
export const RISK_SEVERITY_OPTIONS = [1, 3, 5, 10];
/** 風險等級（低/中/高） */
export const RISK_ANNUAL_LEVELS = ['低', '中', '高'];
/** 風險等級 → 管制方式說明（附表4 註腳口徑） */
export const RISK_ANNUAL_LEVEL_HINTS = {
    低: '低（<20）以定期追蹤查驗方式持續監控',
    中: '中（20-40）加強追蹤查驗頻率並要求限期改善',
    高: '高（>40）列入重點管制對象，增加不定期查驗與市售抽樣',
};
// ─── 單項評分 schema ─────────────────────────────────────────────────────────
/**
 * 單一評估項評分（存於 risk_assessment_annual.items jsonb 陣列）。
 * - score：0-3 分
 * - scoreNote：評分說明（AI 判定依據或人工備註）
 * - severityNote：嚴重度補充說明（可選）
 * - source：'ai' AI 自動判定 / 'manual' 人工修正
 */
export const riskAnnualItemSchema = z.object({
    key: riskAnnualItemKeySchema,
    label: z.string().min(1).max(100),
    score: z.number().int().min(0).max(3),
    scoreNote: z.string().max(500).optional(),
    severityNote: z.string().max(500).optional(),
    source: z.enum(['ai', 'manual']),
});
// ─── 請求 schema ─────────────────────────────────────────────────────────────
/** 建立單客戶年度評估請求（POST /api/risk-annual） */
export const createRiskAnnualSchema = z.object({
    clientId: z.number().int().positive(),
    year: z.number().int().min(2000).max(2200),
    /** 評估日期（ISO date，缺省為建立當日） */
    assessedDate: z.iso.date().optional(),
});
/** 批量生成本年度評估請求（POST /api/risk-annual/batch）；clientIds 缺省 = 全部啟用客戶 */
export const batchCreateRiskAnnualSchema = z.object({
    year: z.number().int().min(2000).max(2200),
    clientIds: z.array(z.number().int().positive()).max(500).optional(),
});
/**
 * 更新評估請求（PATCH /api/risk-annual/:id）。
 * - items：按 key 局部修正分數/說明（人工修正後 source 轉 'manual'）
 * - severity / B 區（controlMeasures/controlStatus）/ notes 均可選
 * - subtotal / total / level 由服務端重算，不接受客戶端提交
 */
export const updateRiskAnnualSchema = z.object({
    assessedDate: z.iso.date().optional(),
    items: z
        .array(z.object({
        key: riskAnnualItemKeySchema,
        score: z.number().int().min(0).max(3),
        scoreNote: z.string().max(500).nullish(),
        severityNote: z.string().max(500).nullish(),
    }))
        .max(RISK_ANNUAL_ITEM_KEYS.length)
        .optional(),
    severity: z.union([z.literal(1), z.literal(3), z.literal(5), z.literal(10)]).optional(),
    controlMeasures: z.string().max(2000).nullish(),
    controlStatus: z.string().max(2000).nullish(),
    notes: z.string().max(2000).nullish(),
});
/** 列表查詢參數（GET /api/risk-annual） */
export const riskAnnualListQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
    year: z.coerce.number().int().min(2000).max(2200).optional(),
    clientId: z.coerce.number().int().positive().optional(),
    level: z.enum(RISK_ANNUAL_LEVELS).optional(),
    /** 客戶名稱 / 註冊碼模糊搜尋 */
    search: z.string().max(100).optional(),
});
// ─── 純函數 ──────────────────────────────────────────────────────────────────
/**
 * 依合計分數判定風險等級。
 * 口徑（附表4）：低（<20）／中（20-40，含邊界）／高（>40）。
 */
export function calcRiskLevel(total) {
    if (total > 40)
        return '高';
    if (total >= 20)
        return '中';
    return '低';
}
/**
 * 計算小計與合計。
 * - subtotal = Σ 各項 score
 * - total = subtotal × severity（嚴重度=1 時小計=合計，即截圖常態）
 */
export function calcTotals(items, severity) {
    const subtotal = items.reduce((sum, it) => sum + it.score, 0);
    return { subtotal, total: subtotal * severity };
}
/**
 * 次年度遞減預填：今年分數高，明年逐年下降（3→2→1→0，到 0 為止）。
 * 對超界輸入做鉗位（<0 視為 0，>3 視為 3）保證輸出恆在 0-3。
 */
export function carryOverScore(prev) {
    const clamped = Math.max(0, Math.min(3, Math.trunc(prev)));
    return Math.max(0, clamped - 1);
}
/**
 * 由上一年度 items 生成新年度預填 items（逐年遞減）。
 * - 每項分數 carryOverScore 遞減，source 標 'ai'（自動預填，允許人工再修正）
 * - scoreNote 記錄遞減來源，便於追溯
 * - 上一年度缺某項（歷史資料不全）時該項按 0 分預填
 */
export function buildCarryOverItems(prevItems, prevYear) {
    return RISK_ANNUAL_ITEM_DEFS.map((def) => {
        const prev = prevItems.find((it) => it.key === def.key);
        const prevScore = prev?.score ?? 0;
        const score = carryOverScore(prevScore);
        return {
            key: def.key,
            label: def.label,
            score,
            scoreNote: prev == null
                ? `${prevYear} 年度無此項評分，預填 0 分`
                : `由 ${prevYear} 年度 ${prevScore} 分逐年遞減預填為 ${score} 分`,
            source: 'ai',
        };
    });
}
/**
 * 生成全 0 分的預設 items（無任何歷史數據可依據時的兜底）。
 */
export function buildDefaultItems() {
    return RISK_ANNUAL_ITEM_DEFS.map((def) => ({
        key: def.key,
        label: def.label,
        score: 0,
        scoreNote: '無歷史資料，預設 0 分',
        source: 'ai',
    }));
}
